// Console controls: the segmented control (view or period switch), filter
// chips (narrow a list), and the file drop zone (manual upload). All 44px.
import { useId, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from "react";
import { ConsoleGlyph } from "./icons";
import { formatNumber } from "./format";

export interface ControlOption<V extends string = string> {
  value: V;
  label: string;
}

export interface SegmentedControlProps<V extends string = string> {
  /** Group name for assistive tech, e.g. "Rentang waktu". */
  label: string;
  /** 2 to 5 short options. More, or long labels, belong in a Select. */
  options: ControlOption<V>[];
  value: V;
  onChange: (value: V) => void;
}

/** Single select, .jal-segmented with aria-pressed buttons; the selected segment adds a check and weight 600. */
export function SegmentedControl<V extends string = string>({ label, options, value, onChange }: SegmentedControlProps<V>) {
  return (
    <div className="jal-segmented con-segmented" role="group" aria-label={label}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" title={o.label} aria-pressed={on} onClick={() => onChange(o.value)}>
            <span data-icon="check" aria-hidden="true">
              <ConsoleGlyph name="check" size={20} />
            </span>
            <span className="con-segment-label">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export interface FilterChipsProps<V extends string = string> {
  /** Toolbar name, e.g. "Saring tahap". */
  label: string;
  options: ControlOption<V>[];
  /** Selected values. Empty means no filter. */
  value: V[];
  onChange: (value: V[]) => void;
}

/**
 * Multi-select filter chips in a toolbar: one tab stop, arrow keys move.
 * Selected chips fill layer-2 and show a check; they wrap, never scroll.
 */
export function FilterChips<V extends string = string>({ label, options, value, onChange }: FilterChipsProps<V>) {
  const [focus, setFocus] = useState(0);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const toggle = (v: V) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = options.length - 1;
    let next = focus;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = focus === last ? 0 : focus + 1;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = focus === 0 ? last : focus - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    else return;
    e.preventDefault();
    setFocus(next);
    refs.current[next]?.focus();
  };
  return (
    <div className="con-chips" role="toolbar" aria-label={label} onKeyDown={onKey}>
      {options.map((o, i) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            className="con-chip"
            title={o.label}
            aria-pressed={on}
            tabIndex={i === focus ? 0 : -1}
            onFocus={() => setFocus(i)}
            onClick={() => toggle(o.value)}
          >
            {on ? <ConsoleGlyph name="check" size={20} /> : null}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export type FileDropState = "idle" | "uploading" | "success" | "error";

export interface FileDropProps {
  /** Visible label above the zone, e.g. "Unggah data pendaftar". */
  label: string;
  /** What is accepted, in words: "XLSX atau CSV, maksimal 10 MB". */
  hint: string;
  /** input accept attribute, e.g. ".xlsx,.csv". */
  accept?: string;
  /** Called with the chosen or dropped files. */
  onFiles: (files: File[]) => void;
  state?: FileDropState;
  /** 0 to 100 while uploading; omit for indeterminate. */
  progress?: number;
  /** Status line under the zone: the file name, the result, or the error. */
  message?: ReactNode;
  /** Button text. */
  chooseLabel?: string;
  /** Zone headline. */
  dropLabel?: string;
}

/**
 * Manual upload. The zone accepts a drop; the button opens the picker, so
 * keyboard and screen reader users never need drag and drop. The status row
 * below is reserved, so a message never shifts the layout.
 */
export function FileDrop({
  label,
  hint,
  accept,
  onFiles,
  state = "idle",
  progress,
  message,
  chooseLabel = "Pilih berkas",
  dropLabel = "Tarik berkas ke sini",
}: FileDropProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const busy = state === "uploading";
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    if (busy) return;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) onFiles(files);
  };
  return (
    <div className="con-drop-field">
      <p className="con-drop-label" id={`${id}label`}>
        {label}
      </p>
      <div
        className="con-drop"
        data-over={over ? "" : undefined}
        data-state={state}
        aria-labelledby={`${id}label`}
        aria-describedby={`${id}hint ${id}msg`}
        role="group"
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <span className="con-drop-icon">
          <ConsoleGlyph name="upload" size={24} />
        </span>
        <div className="con-drop-text">
          <p className="con-drop-title">{dropLabel}</p>
          <p className="con-drop-hint" id={`${id}hint`}>
            {hint}
          </p>
        </div>
        <button type="button" className="btn-secondary con-drop-button" disabled={busy} aria-busy={busy || undefined} onClick={() => input.current?.click()}>
          {chooseLabel}
        </button>
        <input
          ref={input}
          className="con-drop-input"
          type="file"
          accept={accept}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? []);
            if (files.length) onFiles(files);
            e.currentTarget.value = "";
          }}
        />
      </div>
      <div className="con-drop-status" id={`${id}msg`} aria-live="polite" data-state={state}>
        {busy ? (
          <div className="jal-progress">
            {typeof progress === "number" ? (
              <progress max={100} value={progress} aria-label={`Mengunggah, ${formatNumber(Math.round(progress))}%`} />
            ) : (
              <progress aria-label="Mengunggah" />
            )}
          </div>
        ) : null}
        {message ? (
          <p className="con-drop-message">
            {state === "error" ? <ConsoleGlyph name="attention" size={16} /> : state === "success" ? <ConsoleGlyph name="good" size={16} /> : null}
            <span>{message}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
}
